import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, CheckCircle, Clock, FileText, AlertTriangle, UploadCloud } from "lucide-react";

import PageHero from "@/components/layout/PageHero";
import { VisualSection } from "@/components/layout/visualLayout";
import SectionHeading from "@/components/common/SectionHeading";
import Reveal from "@/components/common/Reveal";
import api from "@/lib/api";

const formatDate = (iso, locale) => {
  try {
    return new Date(iso).toLocaleString(locale === "en" ? "en-GB" : "de-DE", {
      day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
};

const TIMELINE_LABEL_KEY = {
  submitted: "status.timelineSubmitted",
  ai_analysis_running: "status.timelineAiRunning",
  ai_analysis_completed: "status.timelineAiCompleted",
  ai_analysis_failed: "status.timelineAiFailed",
  in_review: "status.timelineAiCompleted",
  more_info_needed: "status.timelineMoreInfo",
  archived: "status.timelineArchived",
};

const ANALYSIS_LABEL_KEY = {
  pending: "status.analysisPending",
  running: "status.analysisRunning",
  completed: "status.analysisCompleted",
  failed: "status.analysisFailed",
  not_started: "status.analysisNotStarted",
};

const STATUS_ACCENT = {
  submitted: "bg-roseMilk",
  ai_analysis_running: "bg-lavenderFog",
  ai_analysis_completed: "bg-sageMint",
  ai_analysis_failed: "bg-butterCream",
  in_review: "bg-sageMint",
  more_info_needed: "bg-butterCream",
  archived: "bg-sandBeige",
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function StartupStatus() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "de";
  const q = useQuery();
  const token = q.get("token") || "";

  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!token) { setError(true); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get(`/startups/status/${encodeURIComponent(token)}`);
      setData(res.data);
      setError(false);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div data-testid="status-loading">
        <PageHero eyebrow={t("status.eyebrow")} title={t("status.title")} testId="status-hero" />
        <VisualSection testId="status-loading-content">
          <p className="text-center text-warmGrey">{t("status.loading")}</p>
        </VisualSection>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div data-testid="status-error">
        <PageHero eyebrow={t("status.eyebrow")} title={t("status.title")} testId="status-hero" />
        <VisualSection testId="status-error-card">
          <Reveal>
            <div className="max-w-2xl mx-auto wit-card flex flex-col items-start gap-5">
              <div className="w-12 h-12 rounded-full bg-roseMilk text-raspberry flex items-center justify-center">
                <AlertTriangle size={20} />
              </div>
              <SectionHeading title={t("status.errorTitle")} subtitle={t("status.errorBody")} />
              <Link to="/contact" className="wit-btn-primary" data-testid="status-contact-cta">
                <Mail size={16} /> {t("status.errorCta")}
              </Link>
            </div>
          </Reveal>
        </VisualSection>
      </div>
    );
  }

  const accent = STATUS_ACCENT[data.status] || "bg-roseMilk";

  return (
    <div data-testid="page-startup-status">
      <PageHero
        eyebrow={t("status.eyebrow")}
        title={t("status.title")}
        subtitle={t("status.subtitle")}
        testId="status-hero"
      />

      <VisualSection testId="status-content">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3 flex flex-col gap-6">
            <div className="wit-card" data-testid="status-card-main">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h2 className="font-serif text-3xl text-ink">{data.company_name}</h2>
                <span className={`wit-pill border-transparent text-ink ${accent}`} data-testid="status-badge">
                  {data.status_label[lang]}
                </span>
              </div>
              <p className="mt-2 text-warmGrey leading-relaxed">{data.one_liner}</p>

              <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm text-ink">
                <div className="flex items-start gap-3">
                  <Clock size={16} className="text-warmGrey mt-1" />
                  <span>{t("status.submittedAt", { date: formatDate(data.submitted_at, lang) })}</span>
                </div>
                <div className="flex items-start gap-3">
                  {data.deck_uploaded ? (
                    <CheckCircle size={16} className="text-raspberry mt-1" />
                  ) : (
                    <FileText size={16} className="text-warmGrey mt-1" />
                  )}
                  <span>
                    {data.deck_uploaded
                      ? `${t("status.deckUploaded")} · ${t("status.deckVersion", { v: data.deck_version })}`
                      : t("status.deckMissing")}
                  </span>
                </div>
                {data.deck_uploaded && data.deck_size && (
                  <div className="flex items-start gap-3">
                    <FileText size={16} className="text-warmGrey mt-1" />
                    <span>{t("status.deckSize", { mb: (data.deck_size / (1024 * 1024)).toFixed(2) })}</span>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <span className="wit-eyebrow text-plum">{t("status.analysisStatus")}:</span>
                  <span data-testid="analysis-status">
                    {t(ANALYSIS_LABEL_KEY[data.analysis_status] || "status.analysisNotStarted")}
                  </span>
                </div>
              </div>
            </div>

            {data.deck_uploaded && (
              <div className="wit-card" data-testid="status-reupload-section">
                <div className="flex items-center gap-3">
                  <UploadCloud size={18} className="text-raspberry" />
                  <p className="font-serif text-lg text-ink">{t("status.updateDeck")}</p>
                </div>
                <p className="mt-2 text-sm text-warmGrey leading-relaxed">{t("status.subtitle")}</p>
                <p className="mt-3 text-sm text-warmGrey">
                  <Link to="/contact" className="text-raspberry hover:text-coral underline">
                    {t("status.errorCta")}
                  </Link>
                </p>
              </div>
            )}
          </div>

          <aside className="lg:col-span-2 flex flex-col gap-6">
            <div className="wit-card" data-testid="status-next-steps">
              <p className="wit-eyebrow text-plum mb-3">{t("status.nextSteps")}</p>
              <p className="text-ink leading-relaxed">{data.next_steps[lang]}</p>
            </div>

            <div className="wit-card" data-testid="status-timeline">
              <p className="wit-eyebrow text-plum mb-4">{t("status.timeline")}</p>
              <ol className="relative border-l border-sandBeige pl-5 flex flex-col gap-5">
                {(data.timeline || []).map((entry, i) => (
                  <li key={i} data-testid={`timeline-item-${i}`}>
                    <div className="absolute -left-[5px] mt-1 w-2.5 h-2.5 rounded-full bg-raspberry" />
                    <p className="text-ink text-sm">
                      {t(TIMELINE_LABEL_KEY[entry.status] || entry.status)}
                    </p>
                    <p className="text-xs text-warmGrey">{formatDate(entry.at, lang)}</p>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </VisualSection>
    </div>
  );
}
